import { IS_MAINNET, LOCALE_EN, LOCALE_VI, RENEC_EXPLORER_URL, RE_USD, SOLSCAN_EXPLORER_URL } from "@/utils/constants"
import { StyleObject, TokenSymbol } from "@/utils/helpers"
import { formatPercent } from "@/utils/numberFormatter"
import BigNumber from "bignumber.js"
import * as styles from "./mySupplyBorrow/styles"

import {
  APS,
  BNB,
  BTC,
  BlockchainNetwork,
  ConstantsHelper,
  ETH,
  GAST,
  JITO_SOL,
  M_SOL,
  MarketConfigType,
  PLUS1,
  RAY,
  RENDER,
  RENEC,
  RE_NGN,
  SOL,
  ST_RENEC,
  USDC,
  USDS,
  USDT,
  VND,
  WBTC,
} from "@renec-foundation/relend-adapter"

export const formatAPY = (apy: BigNumber | number, apyStyles?: StyleObject) =>
  formatPercent(apy, undefined, 2, 0.01 / 100, true, { ...styles.supplyAPYCell, ...apyStyles })

export const formatPriceChangePercent = (priceChange: BigNumber, priceChangeStyles?: StyleObject) => {
  return formatPercent(priceChange, false, 2, 0.01, true, { ...priceChangeStyles, ...styles.supplyPriceChangeCell })
}

export const DEFAULT_VIEW_MORE_STE_LINK = {
  [LOCALE_VI]: "https://renec.org/vi/blog/tinh-nang-supply-to-earn",
  [LOCALE_EN]: "https://renec.org/vi/blog/supply-to-earn-feature",
}

export const VIEW_MORE_STE_LINKS: {
  [key in TokenSymbol]?: {
    [LOCALE_VI]: string
    [LOCALE_EN]: string
  }
} = {
  [RE_USD]: {
    [LOCALE_VI]: "https://renec.org/vi/blog/ky-gui-usdt-nhan-rel",
    [LOCALE_EN]: "https://renec.org/blog/supply-usdt-earn-rel",
  },
}

export const getLinkExplorerAddress = (network: BlockchainNetwork, address: string) => {
  const explorerURL = network === BlockchainNetwork.SOLANA ? SOLSCAN_EXPLORER_URL : RENEC_EXPLORER_URL
  const cluster = IS_MAINNET ? "mainnet" : network === BlockchainNetwork.SOLANA ? "devnet" : "testnet"
  return `${explorerURL}/address/${address}?cluster=${cluster}`
}

export const getSupplyBorrowSortingList = (network: BlockchainNetwork) =>
  ConstantsHelper.isRenecChain(network) ? SUPPLY_BORROW_RENEC_SORTING_LIST : SUPPLY_BORROW_SOLANA_SORTING_LIST

const SUPPLY_BORROW_RENEC_SORTING_LIST = [
  USDS,
  VND,
  RE_NGN,
  RENEC,
  USDT,
  PLUS1,
  APS,
  GAST,
  ST_RENEC,
  BNB,
  ETH,
  BTC,
  SOL,
]
const SUPPLY_BORROW_SOLANA_SORTING_LIST = [USDS, VND, RENEC, USDT, USDC, WBTC, ETH, SOL, M_SOL, JITO_SOL, RAY, RENDER]

export const USDS_RESERVE = (ConstantsHelper.getSolendConfig()[0] as MarketConfigType).reserves.find(
  (r) => r.liquidityToken.symbol === USDS,
)!

export interface ObligationProxyTransactionHistory {
  instruction: number
  instructionIndex: number
  market: string
  signature: string
  slot: number
  timestamp: number
  ctokenMint: string
  ctokenQuantity: string
  ctokenDestinationAccount?: string
  liquidityMint: string
  liquidityQuantity: string
  liquiditySymbol: string
  obligationID: string
  obligationOwner: string
  userTransferAuthority: string
  reserveID?: string
}

export interface ObligationProxyHistoryResponse {
  results: ObligationProxyTransactionHistory[]
  next: string | null
}

export const calculateEstimatedEarnedByObligationHistory = (
  obligationHistories: ObligationProxyTransactionHistory[] | undefined,
  currentAPR: BigNumber,
  symbol: string,
  tokenDecimals: number,
) => {
  const WITHDRAW_INSTRUCTION_INDEX = 17 // 17 is withdraw instruction index, 4 is supply instruction index
  if (!obligationHistories || obligationHistories.length === 0) return 0

  const now = Date.now() / 1000
  let totalEarned = new BigNumber(0)
  let currentLiquidity = new BigNumber(0)

  for (let i = 0; i < obligationHistories.length; i++) {
    const tokenSymbol = obligationHistories[i].liquiditySymbol
    if (tokenSymbol !== symbol) continue

    const currentTx = obligationHistories[i]
    const nextTx = obligationHistories[i + 1]

    // Update running liquidity total based on instruction type
    const liquidityChange = new BigNumber(currentTx.liquidityQuantity).shiftedBy(-tokenDecimals)
    // If instruction is withdraw (17), subtract the amount; otherwise add it
    currentLiquidity =
      currentTx.instructionIndex === WITHDRAW_INSTRUCTION_INDEX
        ? currentLiquidity.minus(liquidityChange)
        : currentLiquidity.plus(liquidityChange)

    // Calculate duration until next event
    const startTime = currentTx.timestamp
    const endTime = nextTx ? nextTx.timestamp : now
    const durationInSeconds = endTime - startTime

    // Calculate earned interest for this period
    // APR is annual, so divide by seconds in a year
    const periodRate = currentAPR.dividedBy(365 * 24 * 60 * 60)
    const periodEarned = currentLiquidity.multipliedBy(periodRate).multipliedBy(durationInSeconds)

    totalEarned = totalEarned.plus(periodEarned)
  }

  return totalEarned.toNumber()
}
